import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Search, Filter, X, FileEdit, Clock, CheckCircle, XCircle, FileX } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';

const getChangeSummary = (editRequest) => {
  const parts = [];
  const fields = Object.keys(editRequest.requestedChanges || {});
  if (fields.length > 0) parts.push(`Fields: ${fields.join(', ')}`);
  const docs = [];
  if (editRequest.documentChanges?.landRegistryDocument) docs.push('Land Registry');
  if (editRequest.documentChanges?.registryMapDocument) docs.push('Registry Map');
  if (editRequest.documentChanges?.scannedDocuments?.length) docs.push(`${editRequest.documentChanges.scannedDocuments.length} supporting`);
  if (docs.length > 0) parts.push(`Docs: ${docs.join(', ')}`);
  return parts.join(' | ') || 'No changes recorded';
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'Approved': return 'bg-green-100 text-green-800';
    case 'Rejected': return 'bg-red-100 text-red-800';
    case 'Cancelled': return 'bg-gray-100 text-gray-800';
    default: return 'bg-yellow-100 text-yellow-800';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Approved': return <CheckCircle className="h-4 w-4" />;
    case 'Rejected':
    case 'Cancelled': return <XCircle className="h-4 w-4" />;
    default: return <Clock className="h-4 w-4" />;
  }
};

const EditRequestsList = () => {
  const { user } = useAuth();
  const defaultTab = user?.role === 'bo' ? 'initiated' : 'assigned';

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [filters, setFilters] = useState({ status: '', subdivisionId: '', search: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const { data: subdivisionsData } = useQuery(
    'subdivisions',
    async () => {
      const response = await api.get('/subdivisions');
      return response.data;
    },
    { enabled: user?.role === 'dc' }
  );

  const { data, isLoading, error } = useQuery(
    ['editRequests', activeTab, filters, currentPage],
    async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);

      if (activeTab === 'assigned') params.append('assignedTo', user.id);
      else if (activeTab === 'initiated') params.append('initiatedBy', user.id);
      else if (activeTab === 'approved') params.append('status', 'Approved');
      else if (activeTab === 'rejected') params.append('status', 'Rejected');

      if (filters.status && activeTab === 'all') params.append('status', filters.status);
      if (filters.subdivisionId) params.append('subdivisionId', filters.subdivisionId);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/edit-requests/list?${params}`);
      return response.data;
    }
  );

  const { data: pendingCountData } = useQuery(
    'pendingEditRequestsCount',
    async () => {
      const response = await api.get('/edit-requests/pending/count');
      return response.data;
    },
    { refetchInterval: 30000 }
  );

  const editRequests = data?.editRequests || [];
  const pagination = data?.pagination || {};
  const subdivisions = subdivisionsData?.subdivisions || [];
  const pendingCount = pendingCountData?.count || 0;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setFilters({ status: '', subdivisionId: '', search: '' });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ status: '', subdivisionId: '', search: '' });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.status || filters.subdivisionId || filters.search;

  const tabs = [
    ...(user?.role !== 'bo' ? [{ key: 'assigned', label: 'Assigned to Me', showBadge: true }] : []),
    { key: 'initiated', label: 'Initiated by Me' },
    { key: 'all', label: 'All Requests' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  const emptyMessages = {
    assigned: 'No edit requests are currently assigned to you.',
    initiated: 'You have not initiated any edit requests.',
    all: 'No edit requests match your criteria.',
    approved: 'No approved edit requests found.',
    rejected: 'No rejected edit requests found.',
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Requests</h1>
        <p className="text-gray-600 mt-1">
          {user?.role === 'dc'
            ? 'Manage permit edit requests across all subdivisions'
            : 'Manage permit edit requests in your subdivision'}
        </p>
      </div>

      {/* Tabs + Filters */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map(({ key, label, showBadge }) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {label}
                {showBadge && pendingCount > 0 && activeTab === key && (
                  <span className="ml-2 bg-red-500 text-white rounded-full px-2 py-0.5 text-xs">
                    {pendingCount}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Permit number or holder name..."
                  className="input pl-10 w-full text-sm"
                />
              </div>
            </div>

            {/* Status filter — only on "All" tab */}
            {activeTab === 'all' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="input w-full text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="Pending_EAC_Review">Pending EAC Review</option>
                  <option value="Pending_SDO_Review">Pending SDO Review</option>
                  <option value="Pending_ADC_Review">Pending ADC Review</option>
                  <option value="Pending_DC_Approval">Pending DC Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            )}

            {/* Subdivision filter — DC only */}
            {user?.role === 'dc' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Subdivision</label>
                <select
                  value={filters.subdivisionId}
                  onChange={(e) => handleFilterChange('subdivisionId', e.target.value)}
                  className="input w-full text-sm"
                >
                  <option value="">All Subdivisions</option>
                  {subdivisions.map(sub => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name} {sub.code ? `(${sub.code})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {hasActiveFilters && (
              <div className="flex items-end">
                <button onClick={handleClearFilters} className="btn btn-sm btn-outline w-full">
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-red-600">Error loading edit requests</p>
          </div>
        ) : editRequests.length === 0 ? (
          <div className="p-12 text-center">
            <FileX className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-base font-medium text-gray-900">No edit requests found</h3>
            <p className="mt-2 text-sm text-gray-600">{emptyMessages[activeTab]}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Holder</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changes Requested</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Initiated By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {editRequests.map(req => (
                    <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/permits/${req.permitId}`}
                          className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                        >
                          {req.permit?.permit_no}
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{req.permit?.name}</div>
                          <div className="text-gray-500">{req.permit?.subdivision?.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs text-gray-600 max-w-xs truncate" title={getChangeSummary(req)}>
                          {getChangeSummary(req)}
                        </p>
                        {req.reason && (
                          <p className="text-xs text-gray-400 mt-0.5 max-w-xs truncate" title={req.reason}>
                            Reason: {req.reason}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(req.status)}`}>
                          {getStatusIcon(req.status)}
                          {req.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="text-gray-900">{req.initiator?.fullName}</div>
                          <div className="text-gray-500">{req.initiatorRole?.toUpperCase()}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(req.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link to={`/edit-requests/${req.id}`} className="btn btn-sm btn-primary">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="btn btn-sm btn-outline disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                      disabled={currentPage === pagination.totalPages}
                      className="btn btn-sm btn-outline disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                      {' - '}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, pagination.totalItems)}</span>
                      {' of '}
                      <span className="font-medium">{pagination.totalItems}</span>
                      {' results'}
                    </p>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {[...Array(pagination.totalPages)].map((_, i) => {
                        const page = i + 1;
                        if (page === 1 || page === pagination.totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === page
                                  ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                        }
                        return null;
                      })}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
                        disabled={currentPage === pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EditRequestsList;
